import {
  TASK_PRIORITY_LABELS,
  TASK_TYPE_LABELS,
  describeRecurrence,
  localDateParts,
  type ColorToken,
  type TaskDTO,
} from '@hearth/shared';
import { clsx } from 'clsx';
import {
  AlignLeft,
  Bell,
  CheckCircle2,
  ExternalLink,
  MapPin,
  Pencil,
  RotateCcw,
  Store,
  Trash2,
  UserRound,
  Users,
  X,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { OwnerAvatar } from '../../components/crm';
import { Badge, Button, IconButton } from '../../components/ui';
import { colorClasses } from '../../lib/colors';
import { reminderLabel } from '../tasks/entryModel';
import { TASK_TYPE_COLORS, TASK_TYPE_ICONS } from '../tasks/taskAppearance';
import { CalendarPopover } from './CalendarPopover';
import { describeWhen, isCompletable, isOverdue } from './model';

export type EntryDetailsPopoverProps = {
  task: TaskDTO;
  anchor: HTMLElement | null;
  color: ColorToken;
  now: Date;
  canEdit: boolean;
  busy: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggleDone: (done: boolean) => void;
};

function Row({ icon: Icon, label, children }: { icon: typeof Store; label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 text-[13px] text-slate-700">
      <Icon size={15} aria-hidden className="mt-0.5 shrink-0 text-slate-400" />
      <div className="min-w-0 flex-1">
        <span className="sr-only">{label}: </span>
        {children}
      </div>
    </div>
  );
}

/** Everything about one entry, with its actions; opened by clicking it anywhere on the calendar. */
export function EntryDetailsPopover({
  task,
  anchor,
  color,
  now,
  canEdit,
  busy,
  onClose,
  onEdit,
  onDelete,
  onToggleDone,
}: EntryDetailsPopoverProps) {
  const navigate = useNavigate();
  const TypeIcon = TASK_TYPE_ICONS[task.type];
  const done = task.status === 'completed';
  const overdue = isOverdue(task, now);
  const firstDay = task.dueAt ? localDateParts(new Date(task.dueAt)) : null;
  const leadPath = task.businessId ? `/leads/${task.businessId}` : null;

  return (
    <CalendarPopover anchor={anchor} onClose={onClose} label={task.title} className="w-[22rem]">
      <div className="flex items-center justify-end gap-0.5 px-2 pt-2">
        {canEdit && <IconButton icon={Pencil} label="Edit" size="sm" onClick={onEdit} />}
        {canEdit && <IconButton icon={Trash2} label="Delete" size="sm" onClick={onDelete} disabled={busy} />}
        <IconButton icon={X} label="Close" size="sm" onClick={onClose} data-focus-skip />
      </div>

      <div className="space-y-3 px-4 pt-1 pb-4">
        <div className="flex gap-3">
          <span aria-hidden className={clsx('mt-1.5 size-3 shrink-0 rounded-sm', colorClasses(color).dot)} />
          <div className="min-w-0 space-y-0.5">
            <h2
              className={clsx(
                'text-base leading-snug font-semibold break-words text-slate-900',
                done && 'text-slate-500 line-through',
              )}
            >
              {task.title}
            </h2>
            <p className="text-[13px] text-slate-600">{describeWhen(task, now)}</p>
            {task.recurrence && firstDay && (
              <p className="text-xs text-slate-500">{describeRecurrence(task.recurrence, firstDay)}</p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 pl-6">
          <Badge color={TASK_TYPE_COLORS[task.type]} icon={TypeIcon}>
            {TASK_TYPE_LABELS[task.type]}
          </Badge>
          {(task.priority === 'high' || task.priority === 'urgent') && (
            <Badge color={task.priority === 'urgent' ? 'red' : 'amber'}>
              {TASK_PRIORITY_LABELS[task.priority]}
            </Badge>
          )}
          {done && (
            <Badge color="emerald" icon={CheckCircle2}>
              Done
            </Badge>
          )}
          {overdue && <Badge color="red">Overdue</Badge>}
        </div>

        {(task.businessId || task.contactName || task.location) && (
          <div className="space-y-2">
            {leadPath && (
              <Row icon={Store} label="Lead">
                <Link
                  to={leadPath}
                  className="font-medium text-slate-900 hover:text-ember-700 hover:underline"
                >
                  {task.businessName ?? 'Lead'}
                </Link>
              </Row>
            )}
            {task.contactName && (
              <Row icon={UserRound} label="Contact">
                {task.contactName}
              </Row>
            )}
            {task.location && (
              <Row icon={MapPin} label="Location">
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(task.location)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="break-words hover:text-ember-700 hover:underline"
                >
                  {task.location}
                </a>
              </Row>
            )}
          </div>
        )}

        <div className="space-y-2">
          <Row icon={UserRound} label="Assigned to">
            <OwnerAvatar userId={task.assignedTo} showName />
          </Row>
          {task.attendeeIds.length > 0 && (
            <Row icon={Users} label="Guests">
              <ul className="space-y-1">
                {task.attendeeIds.map((id) => (
                  <li key={id}>
                    <OwnerAvatar userId={id} showName />
                  </li>
                ))}
              </ul>
            </Row>
          )}
          {task.reminderMinutes !== null && !done && (
            <Row icon={Bell} label="Reminder">
              {reminderLabel(task.reminderMinutes, task.allDay)}
            </Row>
          )}
          {task.description && (
            <Row icon={AlignLeft} label="Description">
              <p className="max-h-40 overflow-y-auto break-words whitespace-pre-wrap">{task.description}</p>
            </Row>
          )}
        </div>

        {((canEdit && isCompletable(task.type)) || leadPath) && (
          <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
            {canEdit &&
              isCompletable(task.type) &&
              (done ? (
                <Button size="sm" icon={RotateCcw} onClick={() => onToggleDone(false)} disabled={busy}>
                  Reopen
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="primary"
                  icon={CheckCircle2}
                  onClick={() => onToggleDone(true)}
                  disabled={busy}
                >
                  Mark done
                </Button>
              ))}
            {leadPath && (
              <Button size="sm" variant="ghost" icon={ExternalLink} onClick={() => void navigate(leadPath)}>
                Open lead
              </Button>
            )}
          </div>
        )}
      </div>
    </CalendarPopover>
  );
}
